// src/services/backend-api-service.ts
import {
  getModelById,
  type ModelDefinition,
  type TokenUsage,
} from "@/lib/types/models";

// Backend API configuration
export const BACKEND_API_URL =
  "https://api.ads2ai.com";

// Request interfaces
export interface BackendAIRequest {
  messages: Array<{ role: "system" | "user" | "assistant"; content: string }>;
  provider: string;
  model: string;
  metadata: {
    userId: string;
    promptId: string;
    activeAccountId?: string;
  };
}

// Response interfaces
export interface BackendHealthResponse {
  status: string;
  timestamp: string;
}

export interface BackendAIResponse {
  success: boolean;
  response?: {
    content: string;
  };
  provider?: string;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  cost?: {
    inputCost: number;
    outputCost: number;
    totalCost: number;
    currency: string;
  };
  error?: string;
}

// Combined response for testing
export interface BackendTestResult {
  success: boolean;
  message: string;
  healthData?: BackendHealthResponse;
  aiData?: BackendAIResponse;
  error?: string;
}

// Error types for better error handling
export type BackendErrorType =
  | "cors"
  | "network"
  | "server"
  | "model-config"
  | "unknown";

export interface BackendError extends Error {
  type: BackendErrorType;
  statusCode?: number;
  originalError?: Error;
}

// Utility to calculate cost locally (fallback)
export function calculateCostLocally(
  promptTokens: number,
  completionTokens: number,
  modelId: string,
): number {
  const model = getModelById(modelId);
  if (!model) return 0;

  const inputCost = (promptTokens / 1000000) * model.costs.inputCost;
  const outputCost = (completionTokens / 1000000) * model.costs.outputCost;
  return inputCost + outputCost;
}

// Health check function
export async function checkBackendHealth(): Promise<BackendHealthResponse> {
  try {
    const response = await fetch(`${BACKEND_API_URL}/api/health`);

    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    const backendError: BackendError = new Error(
      `Health check failed: ${error instanceof Error ? error.message : "Unknown error"}`,
    ) as BackendError;
    backendError.type = "network";
    throw backendError;
  }
}

// AI request function
export async function makeBackendAIRequest(
  request: BackendAIRequest,
): Promise<BackendAIResponse> {
  try {
    const response = await fetch(`${BACKEND_API_URL}/api/ask`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const backendError: BackendError = new Error(
        `AI request failed: ${response.status}`,
      ) as BackendError;
      backendError.type = response.status === 500 ? "server" : "network";
      backendError.statusCode = response.status;
      throw backendError;
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error && "type" in error) {
      throw error; // Re-throw BackendError
    }

    const backendError: BackendError = new Error(
      `AI request failed: ${error instanceof Error ? error.message : "Unknown error"}`,
    ) as BackendError;
    backendError.type = "network";
    backendError.originalError = error instanceof Error ? error : undefined;
    throw backendError;
  }
}

// Comprehensive test function
export async function testBackendConnection(
  modelId: string,
  userId: string,
  testPrompt: string = "What is the highest mountain in the world?",
): Promise<BackendTestResult> {
  try {
    // First try health check
    const healthData = await checkBackendHealth();

    // Then test AI endpoint
    const selectedModel = getModelById(modelId);
    if (!selectedModel) {
      return {
        success: false,
        message: `❌ Invalid model ID: ${modelId}`,
        error: "Invalid model",
      };
    }

    const aiRequest: BackendAIRequest = {
      messages: [{ role: "user", content: testPrompt }],
      provider: selectedModel.provider,
      model: modelId,
      metadata: {
        userId,
        promptId: "backend-connection-test",
      },
    };

    const aiData = await makeBackendAIRequest(aiRequest);

    if (aiData.success) {
      const promptTokens = aiData.usage?.prompt_tokens || 0;
      const completionTokens = aiData.usage?.completion_tokens || 0;

      // Use backend cost calculation if available, otherwise calculate locally
      const backendCost = aiData.cost?.totalCost;
      const localCost = calculateCostLocally(
        promptTokens,
        completionTokens,
        modelId,
      );
      const displayCost = backendCost !== undefined ? backendCost : localCost;

      const successMessage = `✅ Backend is fully working!

🏥 Health Check: ${healthData.status} (${healthData.timestamp})

🤖 AI Response: "${aiData.response?.content || "No content"}"

📊 Usage Stats:
• Provider: ${aiData.provider || selectedModel.provider || "N/A"}
• Model: ${selectedModel.displayName || modelId}
• Prompt tokens: ${promptTokens}
• Completion tokens: ${completionTokens} 
• Total tokens: ${aiData.usage?.total_tokens || "N/A"}

💰 Cost Breakdown:
• Input cost: $${(aiData.cost?.inputCost || (promptTokens / 1000000) * selectedModel.costs.inputCost).toFixed(6)}
• Output cost: $${(aiData.cost?.outputCost || (completionTokens / 1000000) * selectedModel.costs.outputCost).toFixed(6)}
• Total cost: $${displayCost.toFixed(6)} ${aiData.cost?.currency || "USD"}`;

      return {
        success: true,
        message: successMessage,
        healthData,
        aiData,
      };
    } else {
      return {
        success: false,
        message: `❌ AI request failed: ${aiData.error}`,
        healthData,
        aiData,
        error: aiData.error,
      };
    }
  } catch (error) {
    const backendError = error as BackendError;
    const errorMessage = backendError.message || "Unknown error";

    let formattedMessage: string;

    if (
      errorMessage.toLowerCase().includes("cors") ||
      backendError.type === "cors"
    ) {
      formattedMessage = `❌ CORS Error: ${errorMessage}

Backend CORS may not include localhost:3000 in allowed origins.`;
    } else if (
      errorMessage.includes("fetch") ||
      errorMessage.includes("Failed to fetch") ||
      backendError.type === "network"
    ) {
      formattedMessage = `❌ Network Error: Backend server appears to be down.

API URL: ${BACKEND_API_URL}`;
    } else if (
      errorMessage.includes("500") ||
      backendError.statusCode === 500 ||
      backendError.type === "server"
    ) {
      const selectedModel = getModelById(modelId);
      formattedMessage = `❌ 500 Server Error: Model "${selectedModel?.displayName || modelId}" (${selectedModel?.provider}) may not be configured on the backend yet.

According to the API spec, all 6 models should work, but this one might need backend configuration.

Try switching to a different model or check backend logs.`;
    } else {
      formattedMessage = `❌ Backend test failed: ${errorMessage}`;
    }

    return {
      success: false,
      message: formattedMessage,
      error: errorMessage,
    };
  }
}

// Simple AI request for use in components (without the test formatting)
export async function callBackendAI(
  messages: Array<{ role: "system" | "user" | "assistant"; content: string }>,
  modelId: string,
  userId: string,
  promptId: string,
  activeAccountId?: string,
): Promise<{
  success: boolean;
  content?: string;
  usage?: TokenUsage;
  cost?: number;
  error?: string;
}> {
  try {
    const selectedModel = getModelById(modelId);
    if (!selectedModel) {
      return {
        success: false,
        error: `Invalid model ID: ${modelId}`,
      };
    }

    const request: BackendAIRequest = {
      messages,
      provider: selectedModel.provider,
      model: modelId,
      metadata: {
        userId,
        promptId,
        activeAccountId,
      },
    };

    const response = await makeBackendAIRequest(request);

    if (response.success) {
      const usage: TokenUsage = {
        inputTokens: response.usage?.prompt_tokens || 0,
        outputTokens: response.usage?.completion_tokens || 0,
      };

      const cost =
        response.cost?.totalCost ||
        calculateCostLocally(usage.inputTokens, usage.outputTokens, modelId);

      return {
        success: true,
        content: response.response?.content || "",
        usage,
        cost,
      };
    } else {
      return {
        success: false,
        error: response.error || "Unknown error from backend",
      };
    }
  } catch (error) {
    const backendError = error as BackendError;
    return {
      success: false,
      error: backendError.message || "Unknown error occurred",
    };
  }
}
